/**
 * Client-side mirror of backend animation math for preview (deterministic).
 */

import { isNarrationGatedElement } from './layerTypes';

export function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

export function easeOutCubic(t) {
  const x = clamp(t, 0, 1);
  return 1 - Math.pow(1 - x, 3);
}

const ENTRY_ANIM_TYPES = new Set([
  'slide_up',
  'slide_down',
  'slide_left',
  'slide_right',
  'zoom_in',
  'zoom_out',
]);

export function animProgress(currentTime, anim) {
  const start = Number(anim.start) || 0;
  const duration = Math.max(Number(anim.duration) || 0.001, 0.001);
  return clamp((currentTime - start) / duration, 0, 1);
}

export function opacityForElement(elementId, currentTime, animations, element = null) {
  let opacity = 1;
  const skipEntryFade =
    isNarrationGatedElement(element)
    || Boolean(element?.sync_visual_at_scene_start);
  for (const anim of animations || []) {
    if (anim.target !== elementId) continue;
    const p = animProgress(currentTime, anim);
    if (anim.type === 'fade_in' || anim.type === 'crossfade') opacity *= easeOutCubic(p);
    else if (anim.type === 'fade_out') opacity *= 1 - easeOutCubic(p);
    else if (ENTRY_ANIM_TYPES.has(anim.type) && p < 1 && !skipEntryFade) opacity *= easeOutCubic(p);
  }
  return clamp(opacity, 0, 1);
}

export function offsetForElement(elementId, currentTime, animations) {
  let dx = 0;
  let dy = 0;
  let scale = 1;
  for (const anim of animations || []) {
    if (anim.target !== elementId) continue;
    const p = easeOutCubic(animProgress(currentTime, anim));
    const t = anim.type;
    if (t === 'slide_up') {
      if (anim.fromDy != null) {
        const fdy = Number(anim.fromDy);
        const tdy = Number(anim.toDy ?? 0);
        dy += fdy + (tdy - fdy) * p - tdy;
      } else {
        const fy = Number(anim.fromY ?? 650);
        const ty = Number(anim.toY ?? 500);
        dy += fy + (ty - fy) * p - ty;
      }
    } else if (t === 'slide_down') {
      if (anim.fromDy != null) {
        const fdy = Number(anim.fromDy);
        const tdy = Number(anim.toDy ?? 0);
        dy += fdy + (tdy - fdy) * p - tdy;
      }
    } else if (t === 'slide_left') {
      if (anim.fromDx != null) {
        const fdx = Number(anim.fromDx);
        const tdx = Number(anim.toDx ?? 0);
        dx += fdx + (tdx - fdx) * p - tdx;
      } else {
        const fx = Number(anim.fromX ?? 900);
        const tx = Number(anim.toX ?? 640);
        dx += fx + (tx - fx) * p - tx;
      }
    } else if (t === 'slide_right') {
      if (anim.fromDx != null) {
        const fdx = Number(anim.fromDx);
        const tdx = Number(anim.toDx ?? 0);
        dx += fdx + (tdx - fdx) * p - tdx;
      }
    } else if (t === 'zoom_in') {
      const fs = Number(anim.fromScale ?? 0.8);
      const ts = Number(anim.toScale ?? 1);
      scale *= fs + (ts - fs) * p;
    } else if (t === 'zoom_out') {
      const fs = Number(anim.fromScale ?? 1);
      const ts = Number(anim.toScale ?? 0.85);
      scale *= fs + (ts - fs) * p;
    } else if (t === 'pulse' || t === 'glow') {
      if (animProgress(currentTime, anim) < 1) scale *= 1 + 0.05 * Math.sin(currentTime * 8);
    }
  }
  return { dx, dy, scale };
}

export function typewriterText(text, elementId, currentTime, animations, narrEnd = null) {
  if (narrEnd != null && currentTime >= narrEnd) return text || '';
  for (const anim of animations || []) {
    if (anim.target !== elementId || anim.type !== 'typewriter') continue;
    const start = Number(anim.start) || 0;
    const cps = Number(anim.charsPerSecond) || 12;
    const visible = Math.floor(Math.max(0, currentTime - start) * cps);
    return (text || '').substring(0, visible);
  }
  return text || '';
}

export function cursorSuffix(elementId, currentTime, animations) {
  for (const anim of animations || []) {
    if (anim.target !== elementId || anim.type !== 'cursor_blink') continue;
    return Math.floor(currentTime * 2) % 2 === 0 ? '|' : '';
  }
  return '';
}
